import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter,
  // `Redirect` was a react-router v5 export and was removed in v6 (replaced
  // by <Navigate />). It was being imported but never used in this file.
  Route,
  Routes,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import TaskydoLogin, { PasswordRecovery } from "../../components/auth/TaskydoLogin";
import HandoffPage from "@taskydo/web-core/HandoffPage.jsx";
import { clearSession, loadMe, readTokens, sessionFromMe } from "../sessionBridge";
import Events from "../../components/events";
import Home from "../../components/home";
import LandingPage from "../../components/landingPage";
import Settings from "../../components/setting";
import routerPath from "./routerPath";
import * as sessionActions from "../../store/session";
import WebinarView from "../../components/webinarView";
import WebinarJoin from "../../components/webinarView/WebinarJoin";
import ChatReports from "../../components/chatReport";
import WebinarAnalytics from "../../components/webinarAnalytics";
import ErrorPage from "../../components/pageNotFound";
import ThankYouPage from "../../components/thankYouPage";
import ForgetPassword from "../../components/auth/ForgetPassword";
import WebinarEdit from "../../components/webinarEdit";
import DummyUsers from "../../components/dummyUsers";
import NormalUser from "../../components/NormalUser";
// New media library page – server <-> per-user S3 transfer + processing tab.
import MediaPage from "../../components/media";
// Documentation / how-to page (S3 setup guide, Media tab usage, FAQ).
import HelpPage from "../../components/help";


export function RequireAuth({ children }) {
  const sessionUser = useSelector((state) => state.session.user);
  const isAuthenticated = sessionUser?.refresh ? true : false; // your logic here
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} />;
  }
  return children;
}
export function RequireJoinWebinar({ children }) {
  const sessionUser = useSelector((state) => state.session.user);
  const isAuthenticated = !!sessionUser;
  const location = useLocation();
  const { webinarId } = useParams();
  const queryParams = new URLSearchParams(location.search);

  useEffect(() => {
    const currentUrl = new URL(window.location.href);
    queryParams.forEach((value, key) => {
      currentUrl.searchParams.set(key, value);
    });
    window.history.replaceState({}, '', currentUrl);
  }, [queryParams]);

  if (!isAuthenticated) {
    console.log("Redirecting due to not being authenticated.");
    return (
      <Navigate
        to={`${routerPath.webinarJoin}/${webinarId}`}
        state={{ from: location }}
      />
    );
  }

  return React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, { queryParams });
    }
    return child;
  });
}
 function Routers() {
  const dispatch = useDispatch();
  const [initialRender, setInitialRender] = React.useState(true);
  const [isAdminUser, setIsAdminUser] = React.useState(" ");
   React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const tokens = readTokens();
      if (tokens.access) {
        try {
          const me = await loadMe(tokens.access);
          if (cancelled) return;
          const session = sessionFromMe(me, tokens);
          localStorage.setItem("user", JSON.stringify(session));
          setIsAdminUser(session.user_role === "admin_user");
          dispatch(sessionActions.setUser(session));
        } catch (err) {
          if (err && err.status === 401) clearSession();
        }
      } else {
        const userInfo = localStorage.getItem("user");
        if (userInfo) {
          const convertData = JSON.parse(userInfo);
          if (!convertData.access) {
            setIsAdminUser(convertData?.user_role === "admin_user");
            dispatch(sessionActions.setUser(convertData));
          }
        }
      }
      if (!cancelled) setInitialRender(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (initialRender) {
    return null;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth/handoff" element={<HandoffPage />} />
        <Route path={routerPath.login} element={<TaskydoLogin />} />
        <Route path={routerPath.signup} element={<TaskydoLogin />} />
        <Route
          path={routerPath.home}
          element={
            <RequireAuth>
              <Home />
            </RequireAuth>
          }
        />
        <Route
          path={routerPath.events}
          element={
            <RequireAuth>
              <Events  />
            </RequireAuth>
          }
        />
        <Route
          path={isAdminUser ? routerPath.normaluser : routerPath.home}
          element={isAdminUser ? <NormalUser /> : null}
        />
        {/* Per-user S3 media library – server <-> S3 transfer + processing.
            Auth is enforced by RequireAuth (we don't gate on isAdminUser
            client-side because the backend already checks request.user). */}
        <Route
          path={routerPath.media}
          element={
            <RequireAuth>
              <MediaPage />
            </RequireAuth>
          }
        />
        {/* Documentation page – S3 setup guide, Media tab walkthrough, FAQ. */}
        <Route
          path={routerPath.help}
          element={
            <RequireAuth>
              <HelpPage />
            </RequireAuth>
          }
        />


        <Route
          path={`${routerPath.chatReport}/:webinarId`}
          element={<ChatReports />}
        />
        <Route
          path={ routerPath.webinarGhostUser }
          element={<DummyUsers />}
        />
        <Route
          path={`${routerPath.webinarAnalytics}/:webinarId`}
          element={<WebinarAnalytics />}
        />
        <Route path={routerPath.setting} element={<Settings />} />
        <Route
          path={`${routerPath.webinarAttendSuccess}/:webinarId`}
          element={<ThankYouPage />}
        />
        <Route
          path={`${routerPath.webinarView}/:webinarId`}
          element={
            <RequireJoinWebinar>
              <WebinarView />
            </RequireJoinWebinar>
          }
        />
        <Route
          path={`${routerPath.webinarEdit}/:webinarId`}
          element={
            <RequireJoinWebinar>
              <WebinarEdit />
            </RequireJoinWebinar>
          }
        />
        <Route
          path={`${routerPath.webinarJoin}/:webinarId`}
          element={<WebinarJoin />}
        />
        <Route path={`${routerPath.errorpage}`} element={<ErrorPage />} />
        <Route
          path={`${routerPath.recoverPassword}`}
          element={<PasswordRecovery />}
        />
        <Route path="/password-recovery" element={<PasswordRecovery />} />
        <Route
          path={`${routerPath.forgetPassword}/:userId/:userToken`}
          element={<ForgetPassword />}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default Routers;
